/**
 * Public Passport (F7.7, V1 boards V1-F7-07a…d): the pack lot a pouch QR names, the server's
 * answer, and how the page reads it. Pure.
 *
 * A pouch QR is https://id.uncompromised.in/10/{lot} (or /01/{gtin}/10/{lot} once we have a
 * GTIN); this app serves the same lot at /p/10/{lot}, /p/01/{gtin}/10/{lot} and /p/{lot}. The lot
 * code ends in a check letter keyed by the server, so the phone only checks its shape — the
 * server says whether it is real.
 *
 * Server: uc-core GET /passport/{code} | /passport/10/{lot} | /passport/01/{gtin}/10/{lot}
 * (passport.controller.ts, passport-view.util.ts). In the usual envelope `data`:
 *  - released, not on hold → { outcome: PASSPORT, product, pack_lot, origin, grown, processed, tested[], label }
 *  - otherwise → { outcome: HELD | RECALLED | NOT_RELEASED | WITHDRAWN | PASSPORT_PREPARING, code, message, contact }
 *  - 404 → NOT_FOUND (unknown code, wrong check letter, GTIN that does not match the lot).
 */
import { z } from 'zod'

export const LOT_CODE_RE = /^PL-\d{4}-\d{4}-\d{4}-[A-HJ-NP-Z2-9]$/

/** Route param (or a pasted link) → an upper-cased lot code, or null when it cannot be one. */
export function normalizeLotCode(raw: string | null | undefined): string | null {
  if (!raw) return null
  let s = raw.trim()
  try {
    s = decodeURIComponent(s)
  } catch {
    /* keep as typed */
  }
  const m = /(?:^|\/)10\/([^/?#]+)\/?$/.exec(s)
  const code = (m ? m[1]! : s).trim().toUpperCase()
  return LOT_CODE_RE.test(code) ? code : null
}

/** A GTIN from the /01/{gtin}/… link form: 8–14 digits, or null when absent or malformed. */
export function normalizeGtin(raw: string | null | undefined): string | null {
  const g = (raw ?? '').trim()
  return /^\d{8,14}$/.test(g) ? g : null
}

const str = z.string()
const optStr = str.nullish()

export const labResultSchema = z.looseObject({
  analyte: str,
  /** As the lab wrote it: "not detected (LOQ 0.01 mg/kg)" or "1.2 µg/kg". */
  result: str,
  pass: z.boolean().default(true),
  loq: z.union([str, z.number()]).transform(String),
  uom: str,
  /** Not sent by the server yet (see the report): the legal limit for a detected analyte. */
  limit: z.union([str, z.number()]).nullish(),
})
export type LabResult = z.infer<typeof labResultSchema>

export const labPanelSchema = z.looseObject({
  panel: str,
  analytes: z.number().int().nullish(),
  results: z.array(labResultSchema).default([]),
})
export type LabPanel = z.infer<typeof labPanelSchema>

export const labReportSchema = z.looseObject({
  lab: optStr,
  accreditation_no: optStr,
  tested_at: optStr,
  overall: optStr,
  report_available: z.boolean().default(false),
  /** Not sent by the server yet: the lab's report number. */
  report_no: optStr,
  panels: z.array(labPanelSchema).default([]),
})
export type LabReport = z.infer<typeof labReportSchema>

export const passportSchema = z.looseObject({
  outcome: z.literal('PASSPORT'),
  product: z.looseObject({
    name: str,
    net_g: z.number().nullish(),
    sku_code: optStr,
  }),
  pack_lot: z.looseObject({
    code: str,
    pack_date: str,
    best_before: str,
    /** Not sent by the server yet: when the quality team released the lot. */
    released_at: optStr,
  }),
  origin: z.looseObject({
    crop: str,
    /** "KHARIF-2026". */
    season: optStr,
    district: optStr,
    /** Only when the farmer's consent is in force. */
    village: optStr,
    /** Only when the consent also allows his name. */
    farmer_name: optStr,
    /** Not sent by the server yet: the farm-gate purchase and godown dates. */
    bought_at: optStr,
    stored_at: optStr,
  }),
  grown: z.looseObject({ practices: z.array(str).default([]) }).default({ practices: [] }),
  processed: z
    .looseObject({
      steps: z
        .array(
          z.looseObject({
            /** English label from the server: Cleaned, Dried, Shelled, Split, Polished, Graded, Sorted, Packed. */
            step: str,
            date: optStr,
            processed_alone: z.boolean().default(true),
            balance_closed: z.boolean().default(true),
          }),
        )
        .default([]),
    })
    .default({ steps: [] }),
  tested: z.array(labReportSchema).default([]),
  label: z.looseObject({ allergen_statement: optStr, claim_text: optStr }).nullish(),
})
export type Passport = z.infer<typeof passportSchema>

export const WARNING_OUTCOMES = ['NOT_RELEASED', 'HELD', 'RECALLED', 'WITHDRAWN', 'PASSPORT_PREPARING'] as const
export type WarningOutcome = (typeof WARNING_OUTCOMES)[number]

/** Every non-Passport answer (and a PASSPORT answer whose content did not parse). */
export const warningSchema = z.looseObject({
  outcome: str,
  code: optStr,
  message: optStr,
  /** The server's PASSPORT_CONTACT: an email address. */
  contact: optStr,
  /** Not sent by the server yet: what the board shows above the lot code, and since when. */
  product: z.looseObject({ name: str, net_g: z.number().nullish() }).nullish(),
  pack_date: optStr,
  since: optStr,
})
export type WarningBody = z.infer<typeof warningSchema>

export const passportResponseSchema = z.union([passportSchema, warningSchema])
export type PassportResponse = z.infer<typeof passportResponseSchema>

export type PassportView =
  | { kind: 'PASSPORT'; code: string; passport: Passport; contact: string | null }
  | { kind: WarningOutcome; code: string; body: WarningBody; contact: string | null }
  | { kind: 'NOT_FOUND'; code: string; contact: string | null }

/**
 * The page for a server answer. Hold first is the server's rule (outcomeFor); the phone only
 * maps the outcome. A PASSPORT outcome without content reads 'being prepared'; an outcome the
 * phone does not know is treated as not released (never as a Passport).
 */
export function toView(code: string, res: PassportResponse): PassportView {
  if (res.outcome === 'PASSPORT') {
    const p = passportSchema.safeParse(res)
    if (p.success) return { kind: 'PASSPORT', code: p.data.pack_lot.code || code, passport: p.data, contact: null }
  }
  const body = res as WarningBody
  const contact = body.contact ?? null
  const shown = body.code ?? code
  if (body.outcome === 'NOT_FOUND') return { kind: 'NOT_FOUND', code: shown, contact }
  if (body.outcome === 'PASSPORT') return { kind: 'PASSPORT_PREPARING', code: shown, body, contact }
  const kind = (WARNING_OUTCOMES as readonly string[]).includes(body.outcome)
    ? (body.outcome as WarningOutcome)
    : 'NOT_RELEASED'
  return { kind, code: shown, body, contact }
}

/** "not detected (LOQ 0.01 mg/kg)" — the server's wording for a result below the LOQ. */
export function isNotDetected(r: Pick<LabResult, 'result'>): boolean {
  return /^not detected/i.test(r.result.trim())
}

/** The most recent lab report (the server sends them oldest first). */
export function latestReport(p: Passport): LabReport | null {
  return p.tested.length ? p.tested[p.tested.length - 1]! : null
}

const isPesticidePanel = (panel: string) => /pestic/i.test(panel)

export interface LabSummary {
  /** Pesticide analytes tested; 0 when the report has no pesticide panel. */
  pesticides: number
  pesticidesFound: number
  /** The LOQ of the pesticide panel (the highest, when they differ), with its unit. */
  loq: string | null
  uom: string | null
  /** Rows for the other panels: each detected analyte, or the panel when nothing was found. */
  rows: Array<{ label: string; result: LabResult | null }>
  total: number
}

export function labSummary(report: LabReport): LabSummary {
  const pest = report.panels.filter((p) => isPesticidePanel(p.panel)).flatMap((p) => p.results)
  let loq: string | null = null
  for (const r of pest) if (loq === null || Number(r.loq) > Number(loq)) loq = r.loq
  const rows: LabSummary['rows'] = []
  for (const p of report.panels) {
    if (isPesticidePanel(p.panel)) continue
    const found = p.results.filter((r) => !isNotDetected(r))
    if (found.length) for (const r of found) rows.push({ label: r.analyte, result: r })
    else if (p.results.length) rows.push({ label: p.panel, result: null })
  }
  return {
    pesticides: pest.length,
    pesticidesFound: pest.filter((r) => !isNotDetected(r)).length,
    loq,
    uom: pest[0]?.uom ?? null,
    rows,
    total: report.panels.reduce((n, p) => n + p.results.length, 0),
  }
}

/** "KHARIF-2026" → { season: 'KHARIF', year: '2026' }. */
export function splitSeason(code: string | null | undefined): { season: string; year: string } | null {
  if (!code) return null
  const m = /^([A-Z_]+)[-_ ]?(\d{4})$/i.exec(code.trim())
  return m ? { season: m[1]!.toUpperCase(), year: m[2]! } : { season: code.trim().toUpperCase(), year: '' }
}

/** Server step labels (passport-view.util STEP_LABELS) the page knows by key. */
export const STEP_KEYS: Record<string, string> = {
  Cleaned: 'CLEAN',
  Dried: 'DRY',
  Shelled: 'DEHULL',
  Split: 'SPLIT',
  Polished: 'POLISH',
  Graded: 'GRADE',
  Sorted: 'SORT',
  Packed: 'PACK',
}
